import { catchError, filter, map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { ActionType, AppLanguage, UnsubscriberClass, IAction, LanguageService } from '@aston/foundation';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AIChatBoxComponent, AIChatPanelService } from '@aston/ai-chat';
import { Component, OnInit, inject, viewChild } from '@angular/core';
import { DebugBarComponent } from '@aston/foundation/components';
import { IUserAvatar, IUserPopover } from '@aston/user-badge';
import { UserPrefsStoreActions } from '@aston/user-prefs';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { ITheme } from '@aston/themes';
import { Store } from '@ngrx/store';

import { injectFeaturesStore, observeFeature } from '../../../factor-features-module/stores/factor-features';
import { UserNotificationsStore } from '../../../notifications-module/stores';
import * as AppStoreSelectors from '../../../root-store/app-store/selectors';
import { TopBarComponent } from '../../components/top-bar/top-bar.component';
import { UserClearanceLevel } from '../../../authentication-module/enums';
import * as AppStoreActions from '../../../root-store/app-store/actions';
import { SHARED_LAYOUT_COMPONENTS } from '../shared-layout-imports';
import { environment } from '../../../../environments/environment';
import { UserService } from '../../../user-module/services';

@Component({
	selector: 'master-layout',
	templateUrl: './master-layout.component.html',
	providers: [UserNotificationsStore],
	imports: [
		TranslateModule,
		TopBarComponent,
		AIChatBoxComponent,
		SHARED_LAYOUT_COMPONENTS,
	]
})
export class MasterLayoutComponent extends UnsubscriberClass implements OnInit {
	protected notifications = inject(UserNotificationsStore)
	protected aiPanels = inject(AIChatPanelService)
	protected userService = inject(UserService)
	protected features = injectFeaturesStore()
	protected i18n = inject(TranslateService)
	protected lang = inject(LanguageService)
	protected store = inject(Store)

	debugBar = viewChild(DebugBarComponent)
	menuCollapsed = false;

	isAIChatOpen$ = this.aiPanels.openPanels$.pipe(map(o => o.side));
	isAIChatOpenOnce$ = this.isAIChatOpen$.pipe(filter(Boolean), shareReplay(1));
	currentLanguage$ = this.lang.language$
	currentUser$ = this.store.select(AppStoreSelectors.selectCurrentUser);
	currentUserAvatar$: Observable<IUserAvatar> = combineLatest([
		this.store.select(AppStoreSelectors.selectCurrentUser),
		this.store.select(AppStoreSelectors.selectCurrentUserAvatar),
	]).pipe(
		filter(([user]) => !!user),
		map(([_, avatar]) => avatar),
	);

	tenant$ = this.store.select(AppStoreSelectors.selectTenant);
	pageOverTitle$ = this.tenant$.pipe(map(tenant => tenant ? tenant.name : ''));
	pageOverLogo$ = observeFeature('customLogoUrl')

	hasImportHistoryClearanceLevel: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.HistoryImport));
	hasManualImportClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.ManualImport));
	hasLetteringClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.Lettering));
	hasPaymentOrderClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.PaymentOrder));
	hasTeamFollowUpClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.TeamFollowUp));
	hasAdministrationToolsClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.AdministrationTools));
	hasSuperAdministrationClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.SuperAdministration));
	hasReadOnlyAccessToAllSuperDebtorsClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.ReadOnlyAccessToAllSuperDebtors));
	hasDunningClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.Dunning));

	themesIsOpened = false;
	notificationsIsOpened = false;
	notificationsIsOpened$ = toObservable(this.notifications.isPanelOpen);
	notificationsHasUnread$ = toObservable(this.notifications.hasUnread);

	userPopoverContent$: Observable<IUserPopover>;

	// Use it to stop notifications refresh and avoid redux devtools flooding
	enableNotificationRefresh: boolean = environment.name !== 'Local';

	ngOnInit() {
		this.notificationsIsOpened$
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe(isOpened => {
				this.themesIsOpened = false
				this.notificationsIsOpened = !!isOpened
			});

		this.isAIChatOpen$
			.pipe(filter(Boolean), takeUntil(this.destroySubscriptions$))
			.subscribe(_ => {
				this.themesIsOpened = false
				this.notificationsIsOpened = false
			});

		this.features.reload()

		this.userPopoverContent$ = this.currentUser$.pipe(
			filter(Boolean),
			switchMap(user => this.userService.getUserPopoverInfos(user.id).pipe(
				take(1),
				catchError(_ => of(null))
			)));
	}

	toggleNotificationPanel() {
		this.aiPanels.toggleSideChat(false)
		this.enableNotificationRefresh = true;
		this.notifications.togglePanel();

		// let's ask for a notification list right now.
		this.notifications.loadNotifications();
	}

	toggleThemesPanel() {
		this.aiPanels.toggleSideChat(false)
		setTimeout(() => {
			this.notificationsIsOpened = false
			this.themesIsOpened = !this.themesIsOpened;
		}, 100)
	}

	handleAction(action: IAction, origin: 'menu' | 'notifications' | 'themes' = action.value) {
		switch (action.type) {
		case ActionType.OPEN:
			this.closePanels()
			break
		case ActionType.TOGGLE:
			if (origin === 'menu') {
				this.menuCollapsed = !this.menuCollapsed;
			} else if (origin === 'notifications') {
				this.toggleNotificationPanel();
			} else if (origin === 'themes') {
				this.toggleThemesPanel();
			}
			break;
		}
	}

	selectLanguage(language: AppLanguage) {
		this.lang.setCurrentLanguage(language)
		firstValueFrom(this.userService.updateUserLanguage(language))
	}

	logout() {
		const logoutAction = AppStoreActions.Logout()
		this.store.dispatch(AppStoreActions.OpenConfirmationModal({textsKey: `Modal.ConfirmationDeconnexion.`}, logoutAction));
	}

	closePanels() {
		this.themesIsOpened = false
		if (this.notificationsIsOpened) {
			this.notifications.closePanel();
		}
	}

	useTheme(theme: ITheme) {
		this.store.dispatch(UserPrefsStoreActions.UpdatePreferredTheme({theme}))
	}
}

